import { KeyboardEvent, useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";

export interface ThresholdInputProps {
  title: string;
  value: number;
  step?: number;
  range?: number[];
  onChange: (value: number) => void;
}

export default function ThresholdInput({
  step = 1,
  title,
  value,
  range,
  onChange,
}: ThresholdInputProps) {
  const withoutRange = !range;

  const initialCurrentValue = withoutRange
    ? value
    : range.indexOf(value) > -1
    ? range.indexOf(value)
    : range.length - 1;

  const [currentValue, setCurrentValue] = useState(initialCurrentValue);
  useEffect(() => {
    setCurrentValue(initialCurrentValue);
  }, [initialCurrentValue]);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLInputElement>(null);

  const min = 0;
  const max = withoutRange ? 1 : range.length - 1;
  const selectedValue = withoutRange ? currentValue : range[currentValue];

  const updateThreshold = () => {
    onChange(selectedValue);
  };

  const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    const arrowKeys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
    if (arrowKeys.includes(e.key)) {
      updateThreshold();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(parseFloat(e.target.value));
  };

  useEffect(() => {
    if (sliderRef.current && tooltipRef.current) {
      const slider = sliderRef.current;

      const sliderWidth = slider.offsetWidth;
      const percentage = (currentValue - min) / (max - min);
      const leftPosition =
        percentage * sliderWidth - tooltipRef.current.offsetWidth / 2 + 16;

      tooltipRef.current.style.left = `${leftPosition}px`;

      // Update the slider background dynamically to show the blue line from the thumb to the right end
      const filledPercentage = ((currentValue - min) / (max - min)) * 100;
      slider.style.setProperty("--filled-percentage", `${filledPercentage}%`);
    }
    //eslint-disable-next-line
  }, [currentValue, min, max]);

  return (
    <div
      className={styles.rangeContainer}
      data-testid="threshold-slider-container"
    >
      <div className={styles.title}>{title}</div>
      <div className={styles.label}>Low</div>
      <div className={styles.inputContainer}>
        {/* Slider Input */}
        <input
          step={step}
          min={min}
          max={max}
          autoFocus
          type="range"
          ref={sliderRef}
          value={currentValue}
          className={styles.input}
          onChange={handleChange}
          onMouseUp={updateThreshold}
          onKeyUpCapture={handleKeyUp}
        />

        {/* Tooltip that follows the thumb */}
        <div
          data-testid="threshold-input-value-tooltip"
          className={styles.tooltip}
          ref={tooltipRef}
        >
          {selectedValue}
        </div>

        {/* Ticks */}
        <div className={styles.ticks}>
          {range?.map((value, index) => (
            <div key={index} className={styles.tick}></div>
          ))}
        </div>
      </div>
      <div className={styles.label}>High</div>
    </div>
  );
}
